import React, { useEffect, useRef } from "react";
import { MessageCircle, X, Send, Sparkles, RotateCcw } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useChatbot } from "./chatbot/useChatbot";
import { ChatMessages } from "./chatbot/ChatMessages";

export const Chatbot = () => {
  const { t, locale } = useI18n();
  const bot = useChatbot(t, locale);
  const [open, setOpen] = React.useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [bot.messages, open, bot.qualStep]);

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Toggle chatbot"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-brand-azure to-brand-orange flex items-center justify-center shadow-[0_8px_32px_rgba(0,102,255,0.5)] hover:scale-105 transition-transform"
      >
        {open ? <X className="w-5 h-5 text-white" /> : <MessageCircle className="w-5 h-5 text-white" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-[400px] h-[600px] max-h-[85vh] glass-strong rounded-3xl flex flex-col overflow-hidden border border-white/[0.12] shadow-[0_24px_64px_rgba(0,0,0,0.7)]"
        >
          <div className="px-5 py-4 border-b border-white/[0.08] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-azure to-brand-orange flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-display text-white">{t.chatbot.title}</div>
              <div className="text-xs text-white/50">{t.chatbot.subtitle}</div>
            </div>
            {bot.mode !== "greeting" && (
              <button
                type="button"
                onClick={bot.restart}
                aria-label={bot.q.restart}
                data-testid="chatbot-restart"
                className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-white/50 hover:text-brand-orange transition-colors"
                title={bot.q.restart}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
          </div>

          <ChatMessages bot={bot} t={t} locale={locale} scrollRef={scrollRef} />

          <form onSubmit={bot.handleSubmit} className="px-4 py-3 border-t border-white/[0.08] flex items-center gap-2">
            <input
              data-testid="chatbot-input"
              value={bot.input}
              onChange={(e) => bot.setInput(e.target.value)}
              placeholder={bot.inputPlaceholder}
              disabled={bot.inputDisabled}
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-brand-azure disabled:opacity-40"
            />
            <button
              data-testid="chatbot-send"
              type="submit"
              disabled={bot.inputDisabled || !bot.input.trim()}
              className="w-10 h-10 rounded-full bg-brand-azure text-white flex items-center justify-center hover:bg-brand-azureSoft disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="text-center text-[10px] font-mono text-white/30 pb-2">{t.chatbot.hint}</div>
        </div>
      )}
    </>
  );
};
